import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";

import PagedTable from "../shared/Tables/PagedTable";
import { IDepollutionQueueRow } from "../../models/Depollution";
import { IVehicle } from "../../models/Vehicle";
import TableVehicleDetails from "../vehicles/VehiclesTable/TableVehicleDetails";
import useDepollution from "../../data/useDepollution";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useNotification from "../notifications/useNotifications";
import { useScannerOverride } from "../../contexts/ScannerContext";

const colHelper = createColumnHelper<IDepollutionQueueRow>();

export default function DepollutionTable() {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { vrmLookupExisting } = useVehiclesMutations();
  const { createDepollution } = useDepollution();
  const depollutionLabel = "Depollution";

  // Resolve a VRM to its depollution (creating one if needed) and open it. When
  // autoStartTimer is true the depollution page starts its timer on arrival (scan).
  const handleLookupAndGo = async (query: string, autoStartTimer = false) => {
    const vrm = query?.replace(/\s/g, "").toUpperCase();
    if (!vrm) {
      addNotification({
        variant: "warn",
        primaryText: "Enter a VRM first",
      });
      return;
    }

    try {
      // Existing vehicles only — a typo'd reg must not conjure a new vehicle here.
      const vehicleResponse = await vrmLookupExisting.mutateAsync(vrm);
      const vehicle = vehicleResponse?.data;

      if (!vehicle?.id) {
        addNotification({
          variant: "warn",
          primaryText: `No vehicle found for ${vrm}`,
          secondaryText: "Check the reg, or add the vehicle first.",
        });
        return;
      }

      if (vehicle.depollutionId) {
        navigate(`/depollution/${vehicle.depollutionId}`, { state: { autoStartTimer } });
        return;
      }

      const depollutionResponse = await createDepollution.mutateAsync({ vehicleId: vehicle.id });
      navigate(`/depollution/${depollutionResponse.data.id}`, { state: { autoStartTimer } });
    } catch (error) {
      addNotification({
        variant: "error",
        primaryText: `Unable to lookup VRM and start ${depollutionLabel.toLowerCase()}`,
      });
    }
  };

  // Scanning a vehicle tag ("v|{vehicleId}|{vrm}") moves into its depollution
  // and starts the timer, mirroring the Station Manager scan flow. We resolve
  // via the scanned VRM, reusing the same lookup as the "Go" button.
  useScannerOverride((scan) => {
    const { parts } = scan;
    if (parts[0]?.toLowerCase() !== "v") return;
    const vrm = parts[2];
    if (!vrm) {
      addNotification({ variant: "warn", primaryText: "Couldn't read the vehicle reg from that scan" });
      return;
    }
    handleLookupAndGo(vrm, true);
  });

  const columns = useMemo(
    () => [
      colHelper.accessor("vrm", {
        id: "Vehicle.Vrm",
        header: "VRM",
      }),
      colHelper.accessor("stockNumber", {
        id: "Vehicle.StockNumber",
        header: "Stock #",
        cell: (info) => info.getValue() ?? "-",
      }),
      colHelper.display({
        id: "vehicleDetails",
        header: "Vehicle",
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          const vehicle = {
            id: row.vehicleId,
            vrm: row.vrm ?? "",
            stockNumber: row.stockNumber ?? "",
            combinedMake: row.combinedMake ?? "",
            combinedModel: row.combinedModel ?? "",
            modelRangeDescription: row.modelRangeDescription ?? "",
            engineModelCode: row.engineModelCode ?? "",
            combinedVIN: "",
            transmission: "",
            fuelType: "",
            vehicleImageUrl: row.vehicleImageUrl ?? "",
          } as IVehicle;

          return <TableVehicleDetails vehicle={vehicle} />;
        },
      }),
      colHelper.accessor("engineModelCode", {
        id: "Vehicle.EngineModelCode",
        header: "Engine Code",
        cell: (info) => info.getValue() ?? "-",
      }),
      colHelper.display({
        id: "status",
        header: depollutionLabel,
        enableSorting: false,
        cell: (info) => (
          <span>{info.row.original.completedSteps}/{info.row.original.totalSteps}</span>
        ),
      }),
    ],
    [depollutionLabel]
  );

  return (
    <PagedTable<IDepollutionQueueRow>
      key="depollution-queue"
      url="/depollution/filtered"
      columns={columns}
      queryLabel="Search VRM, stock, make, model"
      queryButton={{
        label: "Go",
        onClick: handleLookupAndGo,
        isLoading: vrmLookupExisting.isLoading || createDepollution.isLoading,
      }}
      showTotal={true}
      onRowClick={(row) => navigate(`/depollution/${row.id}`)}
    />
  );
}
